<script>
  // @ts-check
  import { afterUpdate, createEventDispatcher, tick } from "svelte";
  import { fade, fly } from "svelte/transition";
  import { trapFocus } from "../utilities/accessibility";
  import { clearBodyLocks, lock } from "tua-body-scroll-lock";
  import Portal from "../atoms/Portal.svelte";
  import Icon from "../atoms/Icon.svelte";
  import Rule from "../atoms/Rule.svelte";

  /** Primarily used for testing
   * @type {string} */
  export let id = undefined;

  /** Indicates whether the drawer is open or closed.
   * @type {boolean} */
  export let open = false;

  const dispatch = createEventDispatcher();

  $: isOpen = open;

  const closeDrawer = () => {
    dispatch("drawerIsOpen", false);
  };

  const handleWindowKeyDown = (event) => {
    if (event.key === "Escape") {
      closeDrawer();
    }
  };

  const animationEnd = () => {
    dispatch("animationEnd");
  };

  let closeButtonRef = undefined;
  let containerRef = undefined;

  afterUpdate(async () => {
    if (containerRef && isOpen) {
      lock(containerRef);
    } else {
      await tick();
      clearBodyLocks();
    }
  });
</script>

<svelte:window on:keydown={handleWindowKeyDown} />
<Portal shouldRender={isOpen} {id}>
  <div
    role="dialog"
    aria-modal="true"
    class="h-[100vh]"
    use:trapFocus={{
      shouldTrapFocus: true,
      triggerElementQuery: ""
    }}
  >
    <div class="relative">
      <div
        aria-label="Close filter"
        role="button"
        tabindex="0"
        class="fixed left-0 top-0 z-[--stack-level-drawer-background] h-full w-full bg-dark-grey opacity-[0.5]"
        transition:fade={{ duration: 300 }}
        on:click={closeDrawer}
        on:keydown={closeDrawer}
      />
      <div
        class="fixed left-0 top-0 z-[--stack-level-drawer] flex h-full w-[300px] flex-col items-center overflow-y-auto bg-light-grey p-7.5 text-white md:w-[304px] lg:w-[452px] lg:p-15 xl:w-[528px]"
        transition:fly={{ x: "-100%" }}
        on:introend={() => closeButtonRef?.focus()}
        on:outroend={animationEnd}
      >
        <div class="relative h-full w-full">
          <div class="flex w-full justify-between pb-5">
            <slot name="header" />
            <button
              id="close-filter-panel"
              bind:this={closeButtonRef}
              class="text-black focus:outline-dashed focus:outline-offset-2 focus-visible:outline-black"
              on:click={closeDrawer}
            >
              <Icon name="close" />
            </button>
          </div>
          <Rule type="thick" theme="white" class="mb-12.5 md:mb-15" />
          <div class="-mx-3 max-h-[calc(100%-180px)] overflow-y-auto px-3" bind:this={containerRef}>
            <slot name="filters" />
          </div>
          <div class="lef-0 absolute bottom-0 w-full bg-light-grey pt-5">
            <slot name="button" />
          </div>
        </div>
      </div>
    </div>
  </div>
</Portal>
